import { apiClient } from "@/lib/apiClient";
import type {
  OrgHierarchyResponse,
  OrgMember,
  TeamUpdateStatusResponse,
  UpdateManagerPayload,
} from "@/types";

/**
 * Organizational Hierarchy Service
 * Handles all org chart-related API calls
 */
export const orgHierarchyService = {
  /**
   * Fetch complete organizational hierarchy for authenticated user's company
   * Returns nested tree structure with all members
   */
  getOrgHierarchy: async (): Promise<OrgHierarchyResponse> => {
    return apiClient.get<OrgHierarchyResponse>("/api/org-hierarchy");
  },

  /**
   * Fetch direct reports for the authenticated user
   * Automatically returns reports for the current user based on their auth token
   */
  getDirectReports: async (): Promise<{ isLeader: boolean; directReports: OrgMember[] }> => {
    return apiClient.get<{ isLeader: boolean; directReports: OrgMember[] }>(
      `/api/org-hierarchy/direct-reports`
    );
  },

  /**
   * Fetch weekly update status + DRI/IC ownership for the authenticated user's team
   * (themselves plus their direct reports), for the current ISO week.
   */
  getTeamUpdateStatus: async (): Promise<TeamUpdateStatusResponse> => {
    return apiClient.get<TeamUpdateStatusResponse>(
      "/api/org-hierarchy/team-update-status"
    );
  },

  /**
   * Update a user's manager assignment
   * Admin-only endpoint
   * @throws Error if not authorized or circular reference detected
   */
  updateManagerAssignment: async (
    payload: UpdateManagerPayload
  ): Promise<{ updatedUser: OrgMember }> => {
    return apiClient.patch<{ updatedUser: OrgMember }>(
      "/api/org-hierarchy/assign-manager",
      payload
    );
  },
};
